import React, { useState, useContext } from "react";
import { MoonIcon, SunIcon } from "@heroicons/react/24/solid";
import {
  AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Tooltip } from "@mui/material";
import Button from "components/UI-lib/Button";
import { WorkspaceStatus, WorkspaceStartStatus } from "models/Workspace.model";
import { EnvironmentStatus } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import {
  Capability,
  stopStartCapability,
  terminateCapability,
} from "models/Capability.model";
import { OrgContext } from "contexts/OrgContext";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import { toast } from "utils/toast";
import { OneClickDeployContext } from "contexts/OneClickDeployContext";
import { StopModal } from "../../Environment/modals/StopModal";
import { DeleteConfirmModal } from "components/Environment/modals/DeleteEnvironmentConfirmModal";
import { FailedStartModal } from "../../Environment/modals/FailedStartModal";
import Modal from "components/Modals/Modal";
import { Environment } from "../../Environment/utils/types";
import {
  isRunningEnvironmentStatus,
  canRequestEnvironmentStart,
  canRequestEnvironmentStop,
  getEnvironmentCloudCredID,
  getEnvironmentStatus,
  getWorkspaceStartFailureMessageFromTasks,
  getWorkspaceStartStatusFromTasks,
  isEnvironmentStartInProgress,
  isEnvironmentStopInProgress,
  isProviderVMRunning,
  normalizeEnvironmentStatus,
} from "../../Environment/utils/environtmentUtils";
import { STORAGE_KEYS } from "constants/localStorage";
import { cn } from "lib/utils";
import { useStorageCostPerHr } from "hooks/environment/useStorageCostPerHr";
import { Power, Square, Trash2 } from "lucide-react";
import { hardwareActionClassNames } from "components/Environment/shared/ComputeDetail/hardwareActionClassNames";

interface EnvironmentActionsProps {
  environment: Environment;
  capabilities: Capability[];
  loadingCapabilities: boolean;
  deleteEnvironment: (environmentId: string) => void;
  loadingDelete: boolean;
  appearance?: "default" | "hardware";
}

const EnvironmentActions: React.FC<EnvironmentActionsProps> = ({
  environment,
  capabilities,
  loadingCapabilities,
  deleteEnvironment,
  loadingDelete,
  appearance = "default",
}) => {
  const [pendingAction, setPendingAction] = useState<"stop" | "start" | null>(
    null,
  );
  const [showStopModal, setShowStopModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStartFailedModal, setShowStartFailedModal] = useState(false);
  const [showSimpleStopModal, setShowSimpleStopModal] = useState(false);

  const { storageCostPerHr, isLoadingCreatorInfo, creator } =
    useStorageCostPerHr(environment);
  const ownerName = creator?.name || creator?.email || "";

  const isStopModalDismissed =
    localStorage.getItem(STORAGE_KEYS.stopFreeTierModalDismissed) === "true" &&
    storageCostPerHr === 0;

  const wsContext = useContext(WorkspaceContext);
  const oneClickDeployContext = useContext(OneClickDeployContext);
  const orgContext = useContext(OrgContext);
  const environmentStatus = getEnvironmentStatus(environment);
  const backendEnvironmentStatus = normalizeEnvironmentStatus(
    environment.status,
  );
  const startStatus = getWorkspaceStartStatusFromTasks(
    environment?.tasks || [],
  );
  const canRequestStartBase = backendEnvironmentStatus
    ? canRequestEnvironmentStart(backendEnvironmentStatus)
    : environmentStatus === WorkspaceStatus.Stopped;
  const canRequestStopBase = backendEnvironmentStatus
    ? canRequestEnvironmentStop(backendEnvironmentStatus)
    : environmentStatus === WorkspaceStatus.Running;
  const providerVMRunning = isProviderVMRunning(environment);
  const canRequestStop =
    canRequestStopBase &&
    (isRunningEnvironmentStatus(backendEnvironmentStatus) || providerVMRunning);
  const canRequestStart =
    canRequestStartBase &&
    (backendEnvironmentStatus === EnvironmentStatus.STOPPED ||
      !providerVMRunning);
  const startInProgress = backendEnvironmentStatus
    ? isEnvironmentStartInProgress(backendEnvironmentStatus)
    : environmentStatus === WorkspaceStatus.Starting;
  const stopInProgress = backendEnvironmentStatus
    ? isEnvironmentStopInProgress(backendEnvironmentStatus)
    : environmentStatus === WorkspaceStatus.Stopping;
  const stopRequestPending = pendingAction === "stop" && canRequestStop;
  const startRequestPending = pendingAction === "start" && canRequestStart;
  const showStartButton =
    canRequestStart || startInProgress || startRequestPending;
  const startButtonLoading = startRequestPending || startInProgress;
  const showStopStartActions =
    environmentStatus !== WorkspaceStatus.Failure ||
    canRequestStart ||
    canRequestStop;

  const stopButtonDisabled =
    !capabilities.includes(stopStartCapability) ||
    !canRequestStop ||
    stopInProgress ||
    startInProgress ||
    environmentStatus === WorkspaceStatus.Deleting ||
    stopRequestPending ||
    startRequestPending ||
    loadingDelete ||
    isLoadingCreatorInfo;

  const startButtonDisabled =
    !capabilities.includes(stopStartCapability) ||
    !canRequestStart ||
    loadingDelete ||
    startButtonLoading ||
    stopRequestPending;
  const deleteRequestPending = environmentStatus === WorkspaceStatus.Deleting;
  const deleteButtonLoading = loadingDelete || deleteRequestPending;
  const deleteBlockedByStatus = backendEnvironmentStatus
    ? backendEnvironmentStatus === EnvironmentStatus.PENDING ||
      backendEnvironmentStatus === EnvironmentStatus.PREPARING ||
      backendEnvironmentStatus === EnvironmentStatus.PROVISIONING ||
      backendEnvironmentStatus === EnvironmentStatus.TERMINATING ||
      backendEnvironmentStatus === EnvironmentStatus.TERMINATED
    : environmentStatus === WorkspaceStatus.Deploying ||
      environmentStatus === WorkspaceStatus.Deleting;
  const deleteButtonDisabled = deleteButtonLoading || deleteBlockedByStatus;
  const hardwareButtonClassName =
    appearance === "hardware" ? hardwareActionClassNames.neutral : undefined;
  const hardwareStartButtonClassName =
    appearance === "hardware" ? hardwareActionClassNames.primary : undefined;
  const hardwareDangerButtonClassName =
    appearance === "hardware" ? hardwareActionClassNames.danger : undefined;

  const stopWorkspace = async () => {
    setShowStopModal(false);
    setPendingAction("stop");
    if (environment) {
      if (
        oneClickDeployContext.environment?.workspaceId ===
        environment.workspaceId
      ) {
        oneClickDeployContext.reset();
      }
      const res = await wsContext.stopWorkspace(environment.workspaceId || "");
      if (res.success) {
        toast.success(
          "Environment is stopping. It may take a few minutes for the status to update.",
        );
      } else {
        toast.error("Unable to stop environment! Try again.", {
          description: res.message,
        });
        setPendingAction(null);
      }
    }
  };

  const startWorkspace = async () => {
    setPendingAction("start");
    if (environment) {
      const res = await wsContext.startWorkspace(environment.workspaceId || "");
      if (res.success) {
        toast.success(
          "Start request sent, it may take a few minutes for the status to update. ",
        );
      } else {
        toast.error("Unable to start environment! Try again.", {
          description: res.message,
        });
        setPendingAction(null);
      }
    }
  };

  const stopButtonDisabledMessage = () => {
    const environmentCloudCredId = getEnvironmentCloudCredID(environment);
    if (environmentCloudCredId && !capabilities.includes(stopStartCapability)) {
      return "This environment does not support stopping and starting";
    } else if (environmentStatus === WorkspaceStatus.Deploying) {
      return "Cannot stop until the instance is deployed";
    } else if (stopInProgress) {
      return "workspace is stopping";
    } else if (startInProgress) {
      return "workspace is starting";
    } else if (environmentStatus === WorkspaceStatus.Deleting) {
      return "workspace is deleting";
    } else if (stopRequestPending || startRequestPending) {
      return "Request is in progress";
    } else if (!canRequestStop) {
      return "Cannot stop from the current status";
    } else {
      return "";
    }
  };

  return (
    <div className="flex flex-col">
      {/* Stop confirmation modal */}
      <StopModal
        show={showStopModal}
        setShow={setShowStopModal}
        onStop={stopWorkspace}
        storageCostPerHr={storageCostPerHr}
      />
      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onDelete={() => {
          if (environment && !deleteButtonDisabled) {
            deleteEnvironment(environment.workspaceId || "");
          }
        }}
        resourceName={environment.name ?? ""}
        orgName={
          orgContext.orgs.find(
            (o) => o.id === environment?.labels?.organizationId,
          )?.name
        }
        ownerName={ownerName}
        ownerLoading={isLoadingCreatorInfo && !!environment?.labels?.userId}
      />
      <FailedStartModal
        logFileContent={getWorkspaceStartFailureMessageFromTasks(
          environment?.tasks || [],
        )}
        show={showStartFailedModal}
        setShow={setShowStartFailedModal}
        onCancel={() => setShowStartFailedModal(false)}
        onSuccess={() => {
          setShowStartFailedModal(false);
        }}
      />
      {/* Simple stop confirmation modal (shown when stop modal is dismissed) */}
      <Modal
        isOpen={showSimpleStopModal}
        setOpen={setShowSimpleStopModal}
        onClose={() => setShowSimpleStopModal(false)}
        title="Stop Environment"
        hideIcon
        body={
          <p className="text-sm text-secondary">
            Stopping this environment will stop compute charges, but{" "}
            {storageCostPerHr > 0
              ? `$${storageCostPerHr.toFixed(2)}/hr in `
              : ""}
            storage charges will continue. Your data will be preserved.
          </p>
        }
        cancelLabel="Cancel"
        confirmLabel="Confirm Stop"
        onSuccess={() => {
          setShowSimpleStopModal(false);
          stopWorkspace();
        }}
      />
      <div className="flex flex-row">
        {!loadingCapabilities ? (
          <>
            {showStopStartActions && (
              <div className="xl:mt-0">
                {!showStartButton && (
                  <span className="block">
                    <Tooltip
                      title={stopButtonDisabledMessage()}
                      placement="top"
                    >
                      <span>
                        <Button
                          className={hardwareButtonClassName}
                          label={
                            stopRequestPending || stopInProgress
                              ? "Stopping..."
                              : "Stop"
                          }
                          type="secondary"
                          loading={stopRequestPending || stopInProgress}
                          onClick={() =>
                            isStopModalDismissed
                              ? setShowSimpleStopModal(true)
                              : setShowStopModal(true)
                          }
                          iconLeft={
                            appearance === "hardware" ? (
                              <Square
                                className="h-3.5 w-3.5"
                                aria-hidden="true"
                              />
                            ) : (
                              <MoonIcon
                                className="-ml-1 mr-2 h-5 w-5 text-gray-400 dark:text-secondary"
                                aria-hidden="true"
                              />
                            )
                          }
                          disabled={stopButtonDisabled}
                        />
                      </span>
                    </Tooltip>
                  </span>
                )}
                {showStartButton && (
                  <span className="hidden sm:block">
                    <Button
                      className={hardwareStartButtonClassName}
                      label={startButtonLoading ? "Starting..." : "Start"}
                      type="primary"
                      loading={startButtonLoading}
                      onClick={startWorkspace}
                      disabled={startButtonDisabled}
                      iconLeft={
                        appearance === "hardware" ? (
                          <Power className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <SunIcon
                            className="-ml-1 mr-2 h-5 w-5 text-white"
                            aria-hidden="true"
                          />
                        )
                      }
                    />
                  </span>
                )}
              </div>
            )}
            {capabilities.includes(terminateCapability) && (
              <span className="ml-3">
                <Button
                  className={hardwareDangerButtonClassName}
                  label={deleteButtonLoading ? "Deleting..." : "Delete"}
                  type="danger"
                  onClick={() => {
                    if (!deleteButtonDisabled) setShowDeleteModal(true);
                  }}
                  loading={deleteButtonLoading}
                  disabled={deleteButtonDisabled}
                  iconLeft={
                    appearance === "hardware" ? (
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    ) : undefined
                  }
                />
              </span>
            )}
          </>
        ) : (
          <>
            <div
              className={cn(
                "flex flex-row text-sm",
                appearance !== "hardware" && "font-mono",
              )}
            >
              <AdjustmentsHorizontalIcon className="h-5 w-6 mr-1" />
              Loading Capabilities...
            </div>
            <span className="ml-3">
              <Button
                className={hardwareDangerButtonClassName}
                label={deleteButtonLoading ? "Deleting..." : "Delete"}
                type="danger"
                onClick={() => {
                  if (!deleteButtonDisabled) setShowDeleteModal(true);
                }}
                loading={deleteButtonLoading}
                disabled={deleteButtonDisabled}
                iconLeft={
                  appearance === "hardware" ? (
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  ) : undefined
                }
              />
            </span>
          </>
        )}
      </div>
      {startStatus === WorkspaceStartStatus.Failure && (
        <div
          className="flex flex-row mt-2 text-xs text-rose-600 cursor-pointer items-center hover:text-rose-400"
          onClick={() => setShowStartFailedModal(true)}
        >
          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
          <span>Previous Start Attempt Failed</span>
        </div>
      )}
    </div>
  );
};

export default EnvironmentActions;
